import {
  ALLOWED_DOCUMENT_EXTENSIONS,
  ALLOWED_NATIVE_EXTENSIONS,
  ALLOWED_PDF_EXTENSIONS,
  DOCUMENT_INDICES,
  DOCUMENT_NATURE_OPTIONS,
  MAX_DOCUMENT_SIZE_BYTES,
  MAX_NATIVE_SIZE_BYTES,
  MAX_PDF_SIZE_BYTES,
  type ReceptionMode,
} from "@etudescarre/constants";
import { Button, cn, Select } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { XIcon } from "@phosphor-icons/react";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import {
  type FieldPathByValue,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import type { paths } from "@/lib/api/schema";
import { combineDateAndTime, nowLocalTime, todayLocal } from "@/lib/utils/date";
import {
  confirmLeaveWithUnsavedChanges,
  useUnsavedChangesGuard,
} from "@/lib/utils/unsaved-changes";
import { useYardContext } from "@/providers/yard-context";
import { useReceptionEmetteurs } from "../use-reception-emetteurs";

// Screen 016: one reception (emetteur, date, mode, bordereau) holding
// documents alone (N13), maquettes alone (N14) and maquettes with the
// documents extracted from them (N15). Saving is best effort: what succeeds
// is kept, only failed items stay on screen for correction. A maquette
// already registered keeps its `versionId`, so a retry only sends its
// remaining documents. In `proposal` mode a rang 2 adds maquettes and their
// documents to its own proposition (N183), without a reception. A document
// alone may also be a version already received, received again (N16).

type Intervention = {
  id: number;
  zoneName: string;
  buildingName: string | null;
  performanceName: string;
};

type ExistingMaquette = {
  id: number;
  number: string | null;
  name: string;
  interventionIds: number[];
};

type ExistingDocument =
  paths["/api/yards/{yardId}/receptions/documents"]["get"]["responses"][200]["content"]["application/json"]["data"]["documents"][number];

type Mode = "new" | "version";
type DocumentMode = Mode | "received";

type DocumentDraft = {
  mode: DocumentMode;
  /** Set for a new version of an existing document, or a version received again. */
  existingDocumentId: number | null;
  /** Set for a version already received, received again. */
  existingVersionId: number | null;
  number: string;
  name: string;
  nature: string;
  index: string;
  dateDocument: string;
  interventionIds: number[];
  pdf: File | null;
  native: File | null;
  paperCopies: string;
  error: string | null;
};

type MaquetteDraft = {
  mode: Mode;
  existingBimModelId: number | null;
  number: string;
  name: string;
  dateEtat: string;
  heureEtat: string;
  interventionIds: number[];
  ifc: File | null;
  native: File | null;
  versionId: number | null;
  error: string | null;
  documents: DocumentDraft[];
};

type FormValues = {
  emetteurContractId: number;
  dateReception: string;
  modeReception: ReceptionMode;
  bordereauNumber: string;
  bordereau: File | null;
  /** Documents received without a maquette. */
  documents: DocumentDraft[];
  maquettes: MaquetteDraft[];
};

type DocumentPath = FieldPathByValue<FormValues, DocumentDraft>;

const MODE_OPTIONS = [
  { label: "Mail", value: "mail" },
  { label: "Courrier", value: "courrier" },
  { label: "Main propre", value: "main_propre" },
] satisfies { label: string; value: ReceptionMode }[];

const INDEX_OPTIONS = DOCUMENT_INDICES.map((idx) => ({
  label: idx,
  value: idx,
}));

const IFC_EXTENSION = "ifc";

const inputClass =
  "w-full rounded-md border border-zinc-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 read-only:bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100";
const labelClass =
  "mb-1 block text-sm font-medium text-zinc-700 dark:text-zinc-300";
const fileInputClass =
  "w-full text-sm text-zinc-500 file:mr-3 file:rounded-md file:border-0 file:bg-blue-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100 dark:text-zinc-400 dark:file:bg-blue-900/20 dark:file:text-blue-400";
const zoneClass =
  "rounded-md border border-zinc-300 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900";
const legendClass =
  "px-1 text-sm font-semibold text-zinc-900 dark:text-zinc-100";
const panelClass =
  "rounded-lg border border-zinc-200 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900";
const panelTitleClass =
  "mb-2 border-b border-zinc-200 pb-2 text-xs font-bold uppercase tracking-wider text-zinc-700 dark:border-zinc-800 dark:text-zinc-300";
const errorClass = "text-sm text-red-600 dark:text-red-400";

function emptyDocument(): DocumentDraft {
  return {
    mode: "new",
    existingDocumentId: null,
    existingVersionId: null,
    number: "",
    name: "",
    nature: "",
    index: "O",
    dateDocument: todayLocal(),
    interventionIds: [],
    pdf: null,
    native: null,
    paperCopies: "",
    error: null,
  };
}

function isBlankDocument(doc: DocumentDraft) {
  return (
    doc.mode === "new" &&
    !doc.number &&
    !doc.name &&
    doc.interventionIds.length === 0 &&
    isNil(doc.pdf) &&
    isNil(doc.native)
  );
}

/** A new version of `existing`, pre-filled from its previous version. */
function documentVersion(existing: {
  documentId: number;
  number: string;
  name: string;
  nature: string | null;
  nextIndex: string;
  interventionIds: number[];
}): DocumentDraft {
  return {
    ...emptyDocument(),
    mode: "version",
    existingDocumentId: existing.documentId,
    number: existing.number,
    name: existing.name,
    nature: existing.nature ?? "",
    index: existing.nextIndex,
    interventionIds: existing.interventionIds,
  };
}

function emptyMaquette(withDocument: boolean): MaquetteDraft {
  return {
    mode: "new",
    existingBimModelId: null,
    number: "",
    name: "",
    dateEtat: todayLocal(),
    heureEtat: nowLocalTime(),
    interventionIds: [],
    ifc: null,
    native: null,
    versionId: null,
    error: null,
    documents: withDocument ? [emptyDocument()] : [],
  };
}

function toFormData(body: Record<string, unknown> | undefined) {
  const fd = new FormData();
  for (const [key, value] of Object.entries(body ?? {})) {
    if (value instanceof File) {
      fd.append(key, value, value.name);
    } else if (isNotNil(value)) {
      fd.append(key, String(value));
    }
  }
  return fd;
}

function fileSize(file: File) {
  return `${(file.size / 1024 / 1024).toFixed(1)} Mo`;
}

export type RegisterReceptionFormProps = {
  onClose: () => void;
  /** Called after everything was saved; defaults to `onClose`. */
  onSaved?: () => void;
  proposal?: boolean;
};

export function RegisterReceptionForm({
  onClose,
  onSaved = onClose,
  proposal = false,
}: RegisterReceptionFormProps) {
  const { yard, contracts: yardContracts } = useYardContext();
  const emetteurs = useReceptionEmetteurs(proposal ? null : yard.id);
  // A rang 2 proposal is emitted by the caller's own rang 2 contract.
  const contracts = proposal
    ? yardContracts.filter((c) => c.type === "travaux_n_2")
    : emetteurs;

  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [existingMaquettes, setExistingMaquettes] = useState<
    ExistingMaquette[]
  >([]);
  const [existingDocuments, setExistingDocuments] = useState<
    ExistingDocument[]
  >([]);
  const [loadingInterventions, setLoadingInterventions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Created on the first save and reused by retries after a partial save.
  const [receptionId, setReceptionId] = useState<number | null>(null);

  const form = useForm<FormValues>({
    defaultValues: {
      emetteurContractId:
        proposal && contracts.length === 1 ? contracts[0].id : 0,
      dateReception: todayLocal(),
      modeReception: "mail",
      bordereauNumber: "",
      bordereau: null,
      documents: [],
      maquettes: proposal ? [emptyMaquette(true)] : [],
    },
  });
  const documentsArray = useFieldArray({
    control: form.control,
    name: "documents",
  });
  const maquettesArray = useFieldArray({
    control: form.control,
    name: "maquettes",
  });

  const selectedContract = form.watch("emetteurContractId");
  const selectedMode = form.watch("modeReception");
  const documents = form.watch("documents");
  const maquettes = form.watch("maquettes");
  const bordereau = form.watch("bordereau");
  const asksPaperCopies = !proposal && selectedMode !== "mail";
  // Registered maquettes and the created reception belong to the chosen
  // emetteur: switching it would send the remaining documents to another
  // contract.
  const hasRegistered =
    isNotNil(receptionId) || maquettes.some((m) => isNotNil(m.versionId));
  // Once created, the reception is saved: only its pending items are a draft.
  const isDirty =
    !proposal &&
    (documents.length > 0 ||
      maquettes.length > 0 ||
      (selectedContract > 0 && isNil(receptionId)));
  useUnsavedChangesGuard(isDirty && !isSaving);

  // An existing document appears once per reception: a new version, or a
  // version already received.
  const usedDocumentIds = new Set(
    [...documents, ...maquettes.flatMap((m) => m.documents)]
      .map((d) => d.existingDocumentId)
      .filter(isNotNil),
  );

  const contractOptions = contracts.map((c) => ({
    label: `${c.performerCompanyName} (${c.code ?? c.type})`,
    value: String(c.id),
  }));

  const fetchContractData = useCallback(
    async (contractId: number) => {
      setLoadingInterventions(true);
      try {
        const query = { contractId: String(contractId) };
        const path = { yardId: String(yard.id) };
        const [interventionsRes, maquettesRes, documentsRes] =
          await Promise.all([
            apiClient.GET("/api/yards/{yardId}/receptions/interventions", {
              params: { path, query },
            }),
            apiClient.GET("/api/yards/{yardId}/receptions/maquettes", {
              params: { path, query },
            }),
            apiClient.GET("/api/yards/{yardId}/receptions/documents", {
              params: { path, query },
            }),
          ]);
        if (isNotNil(interventionsRes.data)) {
          setInterventions(interventionsRes.data.data.interventions);
        }
        if (isNotNil(maquettesRes.data)) {
          setExistingMaquettes(maquettesRes.data.data.maquettes);
        }
        if (isNotNil(documentsRes.data)) {
          setExistingDocuments(documentsRes.data.data.documents);
        }
      } catch (error) {
        console.error("Failed to load reception contract data", error);
        toaster.error({
          title: "Erreur",
          description: "Impossible de charger les données du contrat",
        });
      } finally {
        setLoadingInterventions(false);
      }
    },
    [yard.id],
  );

  useEffect(() => {
    if (selectedContract > 0) {
      fetchContractData(selectedContract);
    }
  }, [selectedContract, fetchContractData]);

  const changeContract = (contractId: number) => {
    form.setValue("emetteurContractId", contractId);
    // Defaults to the mode of the company's latest reception.
    const lastMode = emetteurs.find(
      (e) => e.id === contractId,
    )?.lastReceptionMode;
    if (isNotNil(lastMode)) form.setValue("modeReception", lastMode);
    // Interventions, existing documents and maquettes depend on the contract.
    documentsArray.replace([]);
    maquettesArray.replace(proposal ? [emptyMaquette(true)] : []);
  };

  const handleCancel = async () => {
    if (await confirmLeaveWithUnsavedChanges()) onClose();
  };

  /** Returns an error message, or null when every pending item is complete. */
  const validate = (values: FormValues): string | null => {
    if (values.emetteurContractId <= 0) {
      return proposal
        ? "Sélectionnez votre contrat"
        : "Sélectionnez un émetteur";
    }
    if (values.documents.length === 0 && values.maquettes.length === 0) {
      return "Ajoutez au moins un document ou une maquette";
    }
    const checkDocument = (doc: DocumentDraft, number: number) => {
      if (doc.mode !== "new" && isNil(doc.existingDocumentId)) {
        return `Document #${number} : sélectionnez le document existant`;
      }
      if (doc.mode === "received") {
        return isNil(doc.existingVersionId)
          ? `Document #${number} : sélectionnez l'indice déjà reçu`
          : null;
      }
      if (doc.interventionIds.length === 0) {
        return `Document #${number} : sélectionnez au moins un niveau ou une façade`;
      }
      return null;
    };
    let documentNumber = 0;
    for (const doc of values.documents) {
      documentNumber += 1;
      const invalid = checkDocument(doc, documentNumber);
      if (isNotNil(invalid)) return invalid;
    }
    for (const [i, maquette] of values.maquettes.entries()) {
      const label = i + 1;
      if (isNil(maquette.versionId)) {
        if (maquette.mode === "version" && isNil(maquette.existingBimModelId)) {
          return `Maquette #${label} : sélectionnez la maquette existante`;
        }
        if (maquette.interventionIds.length === 0) {
          return `Maquette #${label} : sélectionnez au moins un niveau ou une façade`;
        }
        if (isNil(combineDateAndTime(maquette.dateEtat, maquette.heureEtat))) {
          return `Maquette #${label} : renseignez une date et une heure d'état valides`;
        }
      }
      for (const doc of maquette.documents) {
        documentNumber += 1;
        const invalid = checkDocument(doc, documentNumber);
        if (isNotNil(invalid)) return invalid;
      }
    }
    return null;
  };

  const openReception = async (values: FormValues): Promise<number | null> => {
    const { data, error } = await apiClient.POST(
      "/api/yards/{yardId}/receptions",
      {
        params: { path: { yardId: String(yard.id) } },
        body: {
          metadata: JSON.stringify({
            emetteurContractId: values.emetteurContractId,
            dateReception: new Date(values.dateReception).toISOString(),
            modeReception: values.modeReception,
            ...(values.bordereauNumber
              ? { bordereauNumber: values.bordereauNumber }
              : {}),
          }),
          bordereau: values.bordereau ?? undefined,
        },
        bodySerializer: toFormData,
      },
    );
    if (error || isNil(data)) {
      toaster.error({
        title: "Erreur",
        description: error?.error ?? "Échec de la création de la réception",
      });
      return null;
    }
    return data.data.receptionId;
  };

  // The `{receptionId}` path param of the maquette and document routes is the
  // emetteur contract id; the reception row goes in the document metadata.
  const postMaquette = async (
    emetteurContractId: number,
    reception: number | null,
    maquette: MaquetteDraft,
  ): Promise<{ versionId: number } | { error: string }> => {
    const metadata = {
      number: maquette.number,
      name: maquette.name,
      dateEtat: combineDateAndTime(maquette.dateEtat, maquette.heureEtat),
      interventionIds: maquette.interventionIds,
      ...(maquette.mode === "version" && isNotNil(maquette.existingBimModelId)
        ? { existingBimModelId: maquette.existingBimModelId }
        : {}),
      ...(isNotNil(reception) ? { receptionId: reception } : {}),
    };
    try {
      const { data, error } = await apiClient.POST(
        "/api/yards/{yardId}/receptions/{receptionId}/maquettes",
        {
          params: {
            path: {
              yardId: String(yard.id),
              receptionId: String(emetteurContractId),
            },
          },
          body: {
            metadata: JSON.stringify(metadata),
            ifc: maquette.ifc ?? undefined,
            native: maquette.native ?? undefined,
          },
          bodySerializer: toFormData,
        },
      );
      if (error || isNil(data)) {
        return { error: error?.error ?? "Échec de l'enregistrement" };
      }
      return { versionId: data.data.versionId };
    } catch (error) {
      console.error("Failed to register maquette", error);
      return { error: "Une erreur est survenue" };
    }
  };

  const postDocument = async (
    emetteurContractId: number,
    reception: number | null,
    bimModelVersionId: number | null,
    doc: DocumentDraft,
  ): Promise<string | null> => {
    if (doc.mode === "received") {
      return isNil(reception) || isNil(doc.existingVersionId)
        ? "Réception ou indice manquant"
        : postReceivedVersion(reception, doc.existingVersionId, doc);
    }
    const metadata = {
      // Required by the API schema, not persisted.
      performanceId: 1,
      number: doc.number,
      dateDocument: new Date(doc.dateDocument).toISOString(),
      name: doc.name,
      ...(doc.nature ? { nature: doc.nature } : {}),
      index: doc.index || "O",
      interventionIds: doc.interventionIds,
      ...(isNotNil(bimModelVersionId) ? { bimModelVersionId } : {}),
      ...(isNotNil(doc.existingDocumentId)
        ? { existingDocumentId: doc.existingDocumentId }
        : {}),
      ...(isNotNil(reception) ? { receptionId: reception } : {}),
      ...(asksPaperCopies && doc.paperCopies
        ? { paperCopies: Number(doc.paperCopies) }
        : {}),
    };
    try {
      const { error } = await apiClient.POST(
        "/api/yards/{yardId}/receptions/{receptionId}/documents",
        {
          params: {
            path: {
              yardId: String(yard.id),
              receptionId: String(emetteurContractId),
            },
          },
          body: {
            metadata: JSON.stringify(metadata),
            pdf: doc.pdf ?? undefined,
            native: doc.native ?? undefined,
          },
          bodySerializer: toFormData,
        },
      );
      return error ? (error.error ?? "Échec de l'enregistrement") : null;
    } catch (error) {
      console.error("Failed to register document", error);
      return "Une erreur est survenue";
    }
  };

  const postReceivedVersion = async (
    reception: number,
    documentVersionId: number,
    doc: DocumentDraft,
  ): Promise<string | null> => {
    try {
      const { error } = await apiClient.POST(
        "/api/yards/{yardId}/receptions/{receptionId}/received-versions",
        {
          params: {
            path: { yardId: String(yard.id), receptionId: String(reception) },
          },
          body: {
            documentVersionId,
            ...(asksPaperCopies && doc.paperCopies
              ? { paperCopies: Number(doc.paperCopies) }
              : {}),
          },
        },
      );
      return error ? (error.error ?? "Échec de l'enregistrement") : null;
    } catch (error) {
      console.error("Failed to register received version", error);
      return "Une erreur est survenue";
    }
  };

  const handleSave = form.handleSubmit(async (values) => {
    const invalid = validate(values);
    if (isNotNil(invalid)) {
      toaster.error({ title: "Erreur", description: invalid });
      return;
    }

    setIsSaving(true);
    try {
      // A rang 2 proposal is deposited in the sas, without a reception.
      let reception = receptionId;
      if (!proposal && isNil(reception)) {
        reception = await openReception(values);
        if (isNil(reception)) return;
        setReceptionId(reception);
      }
      const emetteurContractId = values.emetteurContractId;

      let registered = 0;
      const remainingDocuments: DocumentDraft[] = [];
      for (const doc of values.documents) {
        const error = await postDocument(
          emetteurContractId,
          reception,
          null,
          doc,
        );
        if (isNil(error)) {
          registered += 1;
        } else {
          remainingDocuments.push({ ...doc, error });
        }
      }

      const remainingMaquettes: MaquetteDraft[] = [];
      for (const maquette of values.maquettes) {
        let versionId = maquette.versionId;
        if (isNil(versionId)) {
          const result = await postMaquette(
            emetteurContractId,
            reception,
            maquette,
          );
          if ("error" in result) {
            // Its documents need the version to be linked: keep them all.
            remainingMaquettes.push({
              ...maquette,
              error: result.error,
              documents: maquette.documents.map((d) => ({ ...d, error: null })),
            });
            continue;
          }
          versionId = result.versionId;
          registered += 1;
        }

        const failedDocuments: DocumentDraft[] = [];
        for (const doc of maquette.documents) {
          const error = await postDocument(
            emetteurContractId,
            reception,
            versionId,
            doc,
          );
          if (isNil(error)) {
            registered += 1;
          } else {
            failedDocuments.push({ ...doc, error });
          }
        }
        if (failedDocuments.length > 0) {
          remainingMaquettes.push({
            ...maquette,
            versionId,
            error: null,
            documents: failedDocuments,
          });
        }
      }

      if (remainingDocuments.length === 0 && remainingMaquettes.length === 0) {
        toaster.success({
          title: "Succès",
          description: `${registered} élément(s) enregistré(s)`,
        });
        onSaved();
        return;
      }
      documentsArray.replace(remainingDocuments);
      maquettesArray.replace(remainingMaquettes);
      toaster.error({
        title: "Enregistrement partiel",
        description: `${registered} élément(s) enregistré(s). Corrigez les éléments restants puis enregistrez à nouveau.`,
      });
    } catch (error) {
      console.error("Failed to register reception", error);
      toaster.error({
        title: "Erreur",
        description: "Une erreur est survenue",
      });
    } finally {
      setIsSaving(false);
    }
  });

  const emetteurSelect = (
    <Select
      label={proposal ? "Contrat rang 2" : "Émetteur"}
      options={contractOptions}
      value={selectedContract > 0 ? [String(selectedContract)] : []}
      onValueChange={(details) => {
        const val = details.value[0];
        changeContract(val ? Number(val) : 0);
      }}
      disabled={hasRegistered}
      placeholder="Sélectionnez un contrat"
    />
  );

  const zones = (
    <>
      {documentsArray.fields.map((field, index) => (
        <DocumentZone
          key={field.id}
          path={`documents.${index}`}
          number={index + 1}
          interventions={interventions}
          loadingInterventions={loadingInterventions}
          existingDocuments={existingDocuments}
          usedDocumentIds={usedDocumentIds}
          asksPaperCopies={asksPaperCopies}
          allowsReceived
          onRemove={() => documentsArray.remove(index)}
        />
      ))}
      {maquettesArray.fields.map((field, index) => (
        <MaquetteZone
          key={field.id}
          index={index}
          // Documents are numbered across the whole reception.
          documentOffset={
            documents.length +
            maquettes
              .slice(0, index)
              .reduce((n, m) => n + m.documents.length, 0)
          }
          interventions={interventions}
          loadingInterventions={loadingInterventions}
          existingMaquettes={existingMaquettes}
          existingDocuments={existingDocuments}
          usedDocumentIds={usedDocumentIds}
          yardId={yard.id}
          asksPaperCopies={asksPaperCopies}
          onRemove={() => maquettesArray.remove(index)}
        />
      ))}
    </>
  );

  const addButtons = (
    <div className="flex flex-wrap justify-center gap-2">
      {!proposal && (
        <Button
          type="button"
          outline
          onClick={() => documentsArray.append(emptyDocument())}
          disabled={isSaving || selectedContract <= 0}
        >
          Ajouter un document
        </Button>
      )}
      <Button
        type="button"
        outline
        onClick={() => maquettesArray.append(emptyMaquette(false))}
        disabled={isSaving || selectedContract <= 0}
      >
        Ajouter une maquette
      </Button>
      <Button
        type="button"
        outline
        onClick={() => maquettesArray.append(emptyMaquette(true))}
        disabled={isSaving || selectedContract <= 0}
      >
        Ajouter des documents et la maquette associée
      </Button>
    </div>
  );

  const saveButtons = (
    <div className="flex shrink-0 gap-2">
      <Button type="button" outline onClick={handleCancel} disabled={isSaving}>
        Annuler
      </Button>
      <Button type="submit" disabled={isSaving}>
        {isSaving ? "En cours..." : "Enregistrer"}
      </Button>
    </div>
  );

  if (proposal) {
    return (
      <FormProvider {...form}>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="sticky -top-4 z-10 -mt-4 space-y-2 bg-white pt-4 pb-2 dark:bg-zinc-900">
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={onClose}
                className="text-sm text-blue-600 hover:underline dark:text-blue-400"
              >
                Retour aux actions
              </button>
              {saveButtons}
            </div>
            <h3 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              Ajouter des documents et la maquette associée à la proposition
            </h3>
          </div>
          {contracts.length === 0 ? (
            <p className="text-sm text-zinc-500">
              Aucun contrat travaux rang 2 ne vous permet de proposer des
              maquettes et des documents.
            </p>
          ) : (
            emetteurSelect
          )}
          {selectedContract > 0 && (
            <div className="space-y-4">
              {zones}
              {addButtons}
            </div>
          )}
        </form>
      </FormProvider>
    );
  }

  return (
    <FormProvider {...form}>
      <form
        onSubmit={handleSave}
        className="flex h-full flex-col gap-3 overflow-hidden p-4"
      >
        <div className="flex shrink-0 items-center justify-between gap-4">
          <h1 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            Enregistrer une réception de documents et/ou maquettes
          </h1>
          {saveButtons}
        </div>

        {/* Once created, the reception is reused by retries: its fields are
            frozen. */}
        <fieldset
          disabled={isNotNil(receptionId)}
          className="grid shrink-0 grid-cols-[3fr_2fr] gap-3"
        >
          <section className={panelClass}>
            <h2 className={panelTitleClass}>Détails réception</h2>
            <div className="grid grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)] gap-3">
              {emetteurSelect}
              <div>
                <label htmlFor="dateReception" className={labelClass}>
                  Date de remise
                </label>
                <input
                  id="dateReception"
                  type="date"
                  {...form.register("dateReception")}
                  required
                  className={inputClass}
                />
              </div>
              <Select
                label="Mode de remise"
                options={MODE_OPTIONS}
                value={[selectedMode]}
                onValueChange={(details) => {
                  const mode = MODE_OPTIONS.find(
                    (m) => m.value === details.value[0],
                  )?.value;
                  if (isNotNil(mode)) form.setValue("modeReception", mode);
                }}
                placeholder="Sélectionnez un mode"
              />
            </div>
          </section>
          <section className={panelClass}>
            <h2 className={panelTitleClass}>Bordereau joint</h2>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="bordereauNumber" className={labelClass}>
                  N° de bordereau
                </label>
                <input
                  id="bordereauNumber"
                  type="text"
                  {...form.register("bordereauNumber")}
                  maxLength={255}
                  className={inputClass}
                />
              </div>
              <FileField
                id="bordereauFile"
                label="Fichier"
                allowed={ALLOWED_DOCUMENT_EXTENSIONS}
                maxSize={MAX_DOCUMENT_SIZE_BYTES}
                maxLabel="100 Mo"
                file={bordereau}
                onChange={(file) => form.setValue("bordereau", file)}
              />
            </div>
          </section>
        </fieldset>

        <section
          className={cn(panelClass, "flex min-h-0 flex-1 flex-col gap-3")}
        >
          <h2 className={cn(panelTitleClass, "mb-0 shrink-0")}>
            Documents reçus
          </h2>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-none pr-1">
            {selectedContract <= 0 ? (
              <p className="text-sm text-zinc-500">
                Sélectionnez l'émetteur pour saisir les documents et maquettes
                reçus.
              </p>
            ) : documents.length === 0 && maquettes.length === 0 ? (
              <p className="text-sm text-zinc-500">
                Ajoutez un document, une maquette, ou des documents et la
                maquette associée.
              </p>
            ) : (
              zones
            )}
          </div>
          <div className="shrink-0">{addButtons}</div>
        </section>
      </form>
    </FormProvider>
  );
}

type MaquetteZoneProps = {
  index: number;
  documentOffset: number;
  interventions: Intervention[];
  loadingInterventions: boolean;
  existingMaquettes: ExistingMaquette[];
  existingDocuments: ExistingDocument[];
  usedDocumentIds: Set<number>;
  yardId: number;
  asksPaperCopies: boolean;
  onRemove: () => void;
};

function MaquetteZone({
  index,
  documentOffset,
  interventions,
  loadingInterventions,
  existingMaquettes,
  existingDocuments,
  usedDocumentIds,
  yardId,
  asksPaperCopies,
  onRemove,
}: MaquetteZoneProps) {
  const { control, register, setValue, getValues } =
    useFormContext<FormValues>();
  const maquette = useWatch({ control, name: `maquettes.${index}` });
  const documentsArray = useFieldArray({
    control,
    name: `maquettes.${index}.documents`,
  });
  const path = `maquettes.${index}` as const;
  const idPrefix = `maq-${index}`;
  const label = `Maquette #${index + 1}`;

  const maquetteOptions = existingMaquettes
    .filter((m) => isNotNil(m.number))
    .map((m) => ({ label: `${m.number} — ${m.name}`, value: String(m.id) }));

  /**
   * Keeps the new documents the user typed, drops the new versions of existing
   * documents: which ones may be linked depends on the maquette.
   */
  const manualDocuments = () =>
    getValues(`${path}.documents`).filter((d) => isNil(d.existingDocumentId));

  const selectMode = (mode: Mode) => {
    setValue(`${path}.mode`, mode);
    setValue(`${path}.existingBimModelId`, null);
    setValue(`${path}.number`, "");
    setValue(`${path}.name`, "");
    setValue(`${path}.interventionIds`, []);
    documentsArray.replace(manualDocuments());
  };

  const selectExistingMaquette = async (id: number | null) => {
    const existing = existingMaquettes.find((m) => m.id === id);
    setValue(`${path}.existingBimModelId`, id);
    setValue(`${path}.number`, existing?.number ?? "");
    setValue(`${path}.name`, existing?.name ?? "");
    setValue(`${path}.interventionIds`, existing?.interventionIds ?? []);
    documentsArray.replace(manualDocuments());
    if (isNil(id)) return;

    // One new version proposed per document extracted from the maquette's
    // previous version.
    try {
      const { data } = await apiClient.GET(
        "/api/yards/{yardId}/receptions/maquettes/{bimModelId}/previous-version-documents",
        {
          params: {
            path: { yardId: String(yardId), bimModelId: String(id) },
          },
        },
      );
      if (isNil(data)) return;
      const previous = data.data.documents.map(documentVersion);
      // The blank zone opened with the maquette gives way to these documents.
      documentsArray.replace([
        ...previous,
        ...manualDocuments().filter(
          (d) => previous.length === 0 || !isBlankDocument(d),
        ),
      ]);
    } catch (error) {
      console.error("Failed to load previous version documents", error);
      toaster.error({
        title: "Erreur",
        description:
          "Impossible de pré-remplir les documents de la version précédente",
      });
    }
  };

  if (isNil(maquette)) return null;
  const isRegistered = isNotNil(maquette.versionId);

  // A document is extracted from one maquette only: offer the documents not
  // linked yet, or already linked to this maquette.
  const linkableDocuments = existingDocuments.filter(
    (d) => isNil(d.bimModelId) || d.bimModelId === maquette.existingBimModelId,
  );

  return (
    <fieldset className={zoneClass}>
      <legend className={legendClass}>{label}</legend>
      <ZoneHeader
        removeLabel={`Retirer la ${label}`}
        onRemove={onRemove}
        mode={isRegistered ? null : maquette.mode}
        modeOptions={[
          { value: "new", label: "Nouvelle maquette" },
          {
            value: "version",
            label: "Nouvelle version à une maquette existante",
          },
        ]}
        onModeChange={selectMode}
      />

      {isNotNil(maquette.error) && (
        <p role="alert" className={errorClass}>
          {maquette.error}
        </p>
      )}

      {isRegistered ? (
        <p className="rounded-md bg-green-50 p-2 text-sm text-green-800 dark:bg-green-900/20 dark:text-green-300">
          {`Maquette ${maquette.number} enregistrée`}
        </p>
      ) : (
        <div className="grid grid-cols-4 gap-3">
          {maquette.mode === "version" ? (
            maquetteOptions.length === 0 ? (
              <p className="text-sm text-zinc-500">
                Aucune maquette existante pour ce contrat
              </p>
            ) : (
              <Select
                label="N° de la maquette"
                options={maquetteOptions}
                value={
                  isNotNil(maquette.existingBimModelId)
                    ? [String(maquette.existingBimModelId)]
                    : []
                }
                onValueChange={(details) => {
                  const val = details.value[0];
                  selectExistingMaquette(val ? Number(val) : null);
                }}
                placeholder="Sélectionnez une maquette"
                searchable
              />
            )
          ) : (
            <div>
              <label htmlFor={`${idPrefix}-number`} className={labelClass}>
                N° de la maquette
              </label>
              <input
                id={`${idPrefix}-number`}
                type="text"
                {...register(`${path}.number`)}
                required
                maxLength={255}
                className={inputClass}
              />
            </div>
          )}

          <div>
            <label htmlFor={`${idPrefix}-name`} className={labelClass}>
              Intitulé de la maquette
            </label>
            <input
              id={`${idPrefix}-name`}
              type="text"
              {...register(`${path}.name`)}
              required
              maxLength={255}
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor={`${idPrefix}-date`} className={labelClass}>
              Date d'état
            </label>
            <input
              id={`${idPrefix}-date`}
              type="date"
              {...register(`${path}.dateEtat`)}
              required
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor={`${idPrefix}-time`} className={labelClass}>
              Heure d'état
            </label>
            <input
              id={`${idPrefix}-time`}
              type="time"
              {...register(`${path}.heureEtat`)}
              required
              className={inputClass}
            />
          </div>

          <div className="col-span-2">
            <InterventionSelect
              interventions={interventions}
              loading={loadingInterventions}
              selected={maquette.interventionIds}
              onChange={(ids) => setValue(`${path}.interventionIds`, ids)}
            />
          </div>
          <FileField
            id={`${idPrefix}-ifc`}
            label="Fichier IFC"
            allowed={[IFC_EXTENSION]}
            maxSize={MAX_NATIVE_SIZE_BYTES}
            maxLabel="200 Mo"
            file={maquette.ifc}
            onChange={(file) => setValue(`${path}.ifc`, file)}
          />
          <FileField
            id={`${idPrefix}-native`}
            label="Fichier natif"
            allowed={ALLOWED_NATIVE_EXTENSIONS}
            maxSize={MAX_NATIVE_SIZE_BYTES}
            maxLabel="200 Mo"
            file={maquette.native}
            onChange={(file) => setValue(`${path}.native`, file)}
          />
        </div>
      )}

      {/* Documents extracted from this maquette hang off it, as on the
          mockup. */}
      <div className="mt-3 ml-3 space-y-3 border-l-2 border-zinc-300 pl-4 dark:border-zinc-700">
        {documentsArray.fields.map((field, docIndex) => (
          <DocumentZone
            key={field.id}
            path={`${path}.documents.${docIndex}`}
            number={documentOffset + docIndex + 1}
            interventions={interventions}
            loadingInterventions={loadingInterventions}
            existingDocuments={linkableDocuments}
            usedDocumentIds={usedDocumentIds}
            asksPaperCopies={asksPaperCopies}
            onRemove={() => documentsArray.remove(docIndex)}
          />
        ))}
        <Button
          type="button"
          outline
          onClick={() => documentsArray.append(emptyDocument())}
        >
          Ajouter un document à cette maquette
        </Button>
      </div>
    </fieldset>
  );
}

type DocumentZoneProps = {
  path: DocumentPath;
  number: number;
  interventions: Intervention[];
  loadingInterventions: boolean;
  /** Documents this zone may add a new version to. */
  existingDocuments: ExistingDocument[];
  /** Documents already picked elsewhere in the reception. */
  usedDocumentIds: Set<number>;
  asksPaperCopies: boolean;
  /** Offers "Version déjà reçue" (a document alone, in a reception). */
  allowsReceived?: boolean;
  onRemove: () => void;
};

function DocumentZone({
  path,
  number,
  interventions,
  loadingInterventions,
  existingDocuments,
  usedDocumentIds,
  asksPaperCopies,
  allowsReceived = false,
  onRemove,
}: DocumentZoneProps) {
  const { control, register, setValue } = useFormContext<FormValues>();
  const doc = useWatch({ control, name: path });
  const idPrefix = path.replaceAll(".", "-");
  const label = `Document #${number}`;

  if (isNil(doc)) return null;

  const pickableDocuments =
    doc.mode === "received"
      ? existingDocuments.filter((d) => d.receivedVersions.length > 0)
      : existingDocuments;
  const documentOptions = pickableDocuments
    .filter((d) => !usedDocumentIds.has(d.documentId))
    .map((d) => ({
      label: `${d.number} — ${d.name}`,
      value: String(d.documentId),
    }));
  // The picked document stays listed, so the field shows its number.
  if (isNotNil(doc.existingDocumentId)) {
    documentOptions.unshift({
      label: `${doc.number} — ${doc.name}`,
      value: String(doc.existingDocumentId),
    });
  }

  const reset = (draft: DocumentDraft) => {
    // Files, paper copies and date belong to this reception: keep them.
    setValue(path, {
      ...draft,
      dateDocument: doc.dateDocument,
      pdf: doc.pdf,
      native: doc.native,
      paperCopies: doc.paperCopies,
    });
  };

  const selectMode = (mode: DocumentMode) =>
    reset({ ...emptyDocument(), mode, error: doc.error });

  const pickExisting = (documentId: number) => {
    const picked = existingDocuments.find((d) => d.documentId === documentId);
    if (isNil(picked)) return;
    if (doc.mode !== "received") {
      reset(documentVersion(picked));
      return;
    }
    // Usually the latest version is the one received again.
    reset({
      ...emptyDocument(),
      mode: "received",
      existingDocumentId: picked.documentId,
      existingVersionId: picked.receivedVersions.at(-1)?.versionId ?? null,
      number: picked.number,
      name: picked.name,
    });
  };

  const existingDocument = existingDocuments.find(
    (d) => d.documentId === doc.existingDocumentId,
  );
  const receivedIndexOptions = (existingDocument?.receivedVersions ?? []).map(
    (v) => ({ label: v.index ?? "-", value: String(v.versionId) }),
  );
  // An index already on the document cannot be recorded a second time (N13).
  const indexOptions = INDEX_OPTIONS.map((option) => {
    const taken = existingDocument?.existingIndices.includes(option.value);
    return { ...option, disabled: taken, struck: taken };
  });

  const numberField =
    doc.mode === "new" ? (
      <div>
        <label htmlFor={`${idPrefix}-number`} className={labelClass}>
          N° du document
        </label>
        <input
          id={`${idPrefix}-number`}
          type="text"
          {...register(`${path}.number`)}
          required
          maxLength={255}
          className={inputClass}
        />
      </div>
    ) : (
      <Select
        label="N° du document"
        options={documentOptions}
        value={
          isNotNil(doc.existingDocumentId)
            ? [String(doc.existingDocumentId)]
            : []
        }
        onValueChange={(details) => {
          const val = details.value[0];
          if (val) pickExisting(Number(val));
        }}
        placeholder="Sélectionnez le n° du document"
        searchable
      />
    );

  const paperCopiesField = asksPaperCopies && (
    <div>
      <label htmlFor={`${idPrefix}-paper`} className={labelClass}>
        Exemplaires papier reçus
      </label>
      <input
        id={`${idPrefix}-paper`}
        type="number"
        min={1}
        step={1}
        {...register(`${path}.paperCopies`)}
        className={inputClass}
      />
    </div>
  );

  const modeOptions: { value: DocumentMode; label: string }[] = [
    { value: "new", label: "Nouveau document" },
    { value: "version", label: "Nouvelle version d'un document existant" },
    ...(allowsReceived
      ? [{ value: "received" as const, label: "Version déjà reçue" }]
      : []),
  ];

  return (
    <fieldset className={zoneClass}>
      <legend className={legendClass}>{label}</legend>
      <ZoneHeader
        removeLabel={`Retirer le ${label}`}
        onRemove={onRemove}
        mode={doc.mode}
        modeOptions={modeOptions}
        onModeChange={selectMode}
      />

      {isNotNil(doc.error) && (
        <p role="alert" className={cn(errorClass, "mb-2")}>
          {doc.error}
        </p>
      )}

      {doc.mode === "received" ? (
        // Only the reception link is added: the version keeps its fields and
        // files. Distinct keys remount the inputs on a mode change, or a
        // reused uncontrolled input would keep another field's value.
        <div key="received" className="grid grid-cols-4 gap-3">
          {numberField}
          <Select
            label="Indice"
            options={receivedIndexOptions}
            value={
              isNotNil(doc.existingVersionId)
                ? [String(doc.existingVersionId)]
                : []
            }
            onValueChange={(details) => {
              const val = details.value[0];
              setValue(`${path}.existingVersionId`, val ? Number(val) : null);
            }}
            placeholder="Indice"
          />
          <div className={asksPaperCopies ? undefined : "col-span-2"}>
            <label htmlFor={`${idPrefix}-name`} className={labelClass}>
              Intitulé du document
            </label>
            <input
              id={`${idPrefix}-name`}
              type="text"
              {...register(`${path}.name`)}
              readOnly
              className={inputClass}
            />
          </div>
          {paperCopiesField}
        </div>
      ) : (
        <div key="fields" className="grid grid-cols-4 gap-3">
          {numberField}

          <div className="col-span-2">
            <label htmlFor={`${idPrefix}-name`} className={labelClass}>
              Intitulé du document
            </label>
            <input
              id={`${idPrefix}-name`}
              type="text"
              {...register(`${path}.name`)}
              required
              maxLength={255}
              className={inputClass}
            />
          </div>

          <div>
            <label htmlFor={`${idPrefix}-date`} className={labelClass}>
              Date du document
            </label>
            <input
              id={`${idPrefix}-date`}
              type="date"
              {...register(`${path}.dateDocument`)}
              required
              className={inputClass}
            />
          </div>

          <Select
            label="Nature"
            options={DOCUMENT_NATURE_OPTIONS}
            value={doc.nature ? [doc.nature] : []}
            onValueChange={(details) =>
              setValue(`${path}.nature`, details.value[0] ?? "")
            }
            placeholder="Nature"
          />
          <Select
            label="Indice"
            options={indexOptions}
            value={[doc.index]}
            onValueChange={(details) =>
              setValue(`${path}.index`, details.value[0] ?? "O")
            }
            placeholder="Indice"
          />
          <div className={asksPaperCopies ? undefined : "col-span-2"}>
            <InterventionSelect
              interventions={interventions}
              loading={loadingInterventions}
              selected={doc.interventionIds}
              onChange={(ids) => setValue(`${path}.interventionIds`, ids)}
            />
          </div>
          {paperCopiesField}

          <div className="col-span-2">
            <FileField
              id={`${idPrefix}-pdf`}
              label="Fichier PDF"
              allowed={ALLOWED_PDF_EXTENSIONS}
              maxSize={MAX_PDF_SIZE_BYTES}
              maxLabel="50 Mo"
              file={doc.pdf}
              onChange={(file) => setValue(`${path}.pdf`, file)}
            />
          </div>
          <div className="col-span-2">
            <FileField
              id={`${idPrefix}-native`}
              label="Fichier natif"
              allowed={ALLOWED_NATIVE_EXTENSIONS}
              maxSize={MAX_NATIVE_SIZE_BYTES}
              maxLabel="200 Mo"
              file={doc.native}
              onChange={(file) => setValue(`${path}.native`, file)}
            />
          </div>
        </div>
      )}
    </fieldset>
  );
}

type ZoneHeaderProps<M extends string> = {
  removeLabel: string;
  onRemove: () => void;
  /** Null once the item is registered: its mode can no longer change. */
  mode: M | null;
  modeOptions: { value: M; label: string }[];
  onModeChange: (mode: M) => void;
};

function ZoneHeader<M extends string>({
  removeLabel,
  onRemove,
  mode,
  modeOptions,
  onModeChange,
}: ZoneHeaderProps<M>) {
  return (
    <div className="mb-2 flex items-center justify-between gap-2">
      <div className="flex gap-4">
        {isNotNil(mode) &&
          modeOptions.map((m) => (
            <label key={m.value} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                checked={mode === m.value}
                onChange={() => onModeChange(m.value)}
                className="text-blue-600"
              />
              {m.label}
            </label>
          ))}
      </div>
      <button
        type="button"
        aria-label={removeLabel}
        onClick={onRemove}
        className="flex items-center gap-1 rounded-md border border-zinc-300 px-2 py-1 text-xs text-zinc-700 hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
      >
        <XIcon className="size-3" />
        Annuler
      </button>
    </div>
  );
}

type InterventionSelectProps = {
  interventions: Intervention[];
  loading: boolean;
  selected: number[];
  onChange: (ids: number[]) => void;
};

function InterventionSelect({
  interventions,
  loading,
  selected,
  onChange,
}: InterventionSelectProps) {
  if (loading || interventions.length === 0) {
    return (
      <Field label="Niv. Faç. traités">
        <p className="py-2 text-sm text-zinc-500">
          {loading
            ? "Chargement..."
            : "Aucune intervention trouvée pour ce contrat"}
        </p>
      </Field>
    );
  }
  return (
    <Select
      label="Niv. Faç. traités"
      multiple
      // Niveaux et façades are grouped by building (N13).
      groupBy={(item) => item.group ?? ""}
      options={interventions.map((intv) => ({
        label: `${intv.zoneName} — ${intv.performanceName}`,
        value: String(intv.id),
        group: intv.buildingName ?? "Sans bâtiment",
      }))}
      value={selected.map(String)}
      onValueChange={(details) => onChange(details.value.map(Number))}
      placeholder="Niveaux et façades"
    />
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <span className={labelClass}>{label}</span>
      {children}
    </div>
  );
}

type FileFieldProps = {
  id: string;
  label: string;
  allowed: readonly string[];
  maxSize: number;
  maxLabel: string;
  file: File | null;
  onChange: (file: File) => void;
};

function FileField({
  id,
  label,
  allowed,
  maxSize,
  maxLabel,
  file,
  onChange,
}: FileFieldProps) {
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    if (isNil(picked)) return;
    const ext = picked.name.split(".").pop()?.toLowerCase();
    if (isNil(ext) || !allowed.includes(ext)) {
      setError(`Extension non autorisée. Acceptées : ${allowed.join(", ")}`);
      e.target.value = "";
      return;
    }
    if (picked.size > maxSize) {
      setError(`Le fichier dépasse ${maxLabel}`);
      e.target.value = "";
      return;
    }
    setError(null);
    onChange(picked);
  };

  return (
    <div className="min-w-0">
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      <input
        id={id}
        type="file"
        accept={allowed.map((e) => `.${e}`).join(",")}
        onChange={handleChange}
        className={fileInputClass}
      />
      {isNotNil(error) && (
        <p role="alert" className={cn(errorClass, "mt-1")}>
          {error}
        </p>
      )}
      {isNotNil(file) && (
        <p className="mt-1 truncate text-xs text-zinc-500" title={file.name}>
          {file.name} ({fileSize(file)})
        </p>
      )}
    </div>
  );
}
